import React, { useState, useEffect, useMemo } from "react";
import { Box, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Skeleton, Tab, Tabs } from '@mui/material';
import { getAdminAccessLevel, getUserObject } from "../../../actions/profile/UserActions";
import { getAllSites, getAllSubmissions, getCurrentTimer } from "../../../selectors/AdminSelectors";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { Add, AssessmentOutlined, AssignmentOutlined, EmailOutlined, PersonOutline, PlayArrowOutlined, StoreOutlined, TimerOutlined } from "@mui/icons-material";
import { ContainedButton, TooltipIconButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import { CARD_HEADER_STYLE } from "../../../constants/Styles";
import { formatDateTimeToFancyString, timeAgo } from "../../../utilities/DateUtilities";
import StyledStatusIcon from "../../common/StyledStatusIcon";
import { SUBMISSION_STATUS, SUBMISSION_TYPES_HUMAN_MAP } from "../../../constants/Statuses";
import ViewRequestModal from "../modals/ViewRequestModal";
import AdminAddRequestModal from "../modals/AdminAddRequestModal";
import StyledPage from "../../common/StyledPage";
import StyledSection from "../../common/StyledSection";
import { StyledTextField } from "../../common/StyledTextField";
import { StyledTab, StyledTabs } from "../../common/StyledTabs";
import { getUserId } from "../../../selectors/UserSelectors";
import { getAllTeamMembersForAdmin, getCurrentClockifyTimerForAdmin } from "../../../actions/internal/AdminActions";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";

const AdminComponent = ({ history }) => {

    const [requestInView, setRequestInView] = useState();
    const [openAddRequest, setOpenAddRequest] = useState();
    const [searchQuery, setSearchQuery] = useState();
    const [filterType, setFilterType] = useState(0);
    const [isLoadingMembers, setIsLoadingMembers] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const submissions = getAllSubmissions();
    const clockifyTimer = getCurrentTimer();

    const clockifyTimerId = clockifyTimer && clockifyTimer.id;
    const isLimitedTeamMember = getAdminAccessLevel() <= 20;

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        try {
          setIsLoadingMembers(true);
          await getAllTeamMembersForAdmin(getUserId());
          await getCurrentClockifyTimerForAdmin(getUserId());
        } catch (ex) {
          const err = await ex;
          console.error(err);
          showNotificationBar(NOTIFICATION_TYPE.ERROR, "Unable to load team member information at this time.");
        } finally {
          setIsLoadingMembers(false);
        }
    }

    const handleViewRequestOnClose = () => {
        setRequestInView();
    }

    const handleAddRequestOnClose = () => {
        setOpenAddRequest(false)
    }

    const clockifyTimerStartTime = clockifyTimer && clockifyTimer.timeInterval.start;
    const formatElapsedTime = (totalSeconds) => {
        if (Number.isNaN(totalSeconds)) return 'Invalid time entry';

        const safeSeconds = Math.max(0, totalSeconds);

        const hours = Math.floor(safeSeconds / 3600);
        const minutes = Math.floor((safeSeconds % 3600) / 60);
        const seconds = safeSeconds % 60;

        return [hours, minutes, seconds]
            .map((value) => String(value).padStart(2, '0'))
            .join(':');
    };

    useEffect(() => {
        if (!clockifyTimerStartTime) {
            setElapsedSeconds(0);
            return;
        }

        const updateElapsedTime = () => {
            const startTime = new Date(clockifyTimerStartTime).valueOf();
            const now = new Date().valueOf();

            if (Number.isNaN(startTime) || Number.isNaN(now)) {
                setElapsedSeconds(NaN);
                return;
            }

            const diffInSeconds = Math.floor((now - startTime) / 1000);
            setElapsedSeconds(Math.max(0, diffInSeconds));
        };

        updateElapsedTime();

        const intervalId = setInterval(updateElapsedTime, 1000);

        return () => clearInterval(intervalId);
    }, [clockifyTimerStartTime]);

    const elapsedTimeLabel = useMemo(() => {
        return formatElapsedTime(elapsedSeconds);
    }, [elapsedSeconds]);

    const renderGreeting = () => {
        const today = new Date()
        if (today.getHours() < 6 ) return 'Evening'
        else if (today.getHours() >= 6 && today.getHours() < 12) return 'Morning'
        else if (today.getHours() >= 12 && today.getHours() < 18) return 'Afternoon'
        else if (today.getHours() >= 18) return 'Evening'
    }

    const getFirstName = () => (
        getUserObject() && `${getUserObject().user_personal.first_name}`
    )

    const createSubmissionTitle = (submission) => {
        const type = SUBMISSION_TYPES_HUMAN_MAP[submission.submission_type] || submission.submission_type
        if (submission.submission_identifiers.site) {
            return <><strong>{submission.submission_id.substring(submission.submission_id.length - 6).toUpperCase()}</strong>: {submission.submission_fields ? submission.submission_fields.title : 'Untitled'}</>
        } else if (submission.submission_identifiers.user) {
            return `${submission.submission_id.substring(submission.submission_id.length - 6).toUpperCase()}: ${submission.submission_identifiers.user.first_name} ${submission.submission_identifiers.user.last_name}`
        }
        return type;
    }

    const sortSubmissions = (subs) => {
        subs.sort((a, b) => {
            const awaitingStatus = SUBMISSION_STATUS.AWAITING_DETAILS;
        
            const aIsAwaiting = a.submission_status === awaitingStatus;
            const bIsAwaiting = b.submission_status === awaitingStatus;
        
            // If one is awaiting and the other is not, push the awaiting one to the bottom
            if (aIsAwaiting && !bIsAwaiting) return 1;
            if (!aIsAwaiting && bIsAwaiting) return -1;
        
            // Otherwise, sort by submission_received_epoch ascending
            return a.submission_received_epoch - b.submission_received_epoch;
        });
        return subs;
    }

    const getSubmissions = () => {
        if (!submissions) return [];

        let subs = submissions;

        if (searchQuery) {
            subs = searchSubmissions(submissions, searchQuery);
        }

        if (filterType === 0) { // Filter all assigned to user
            const userId = getUserId();
            subs = subs.filter(s => s.submission_assigned_to === userId);
        } else if (filterType === 1) { // Unassigned only
            subs = subs.filter(s => !s.submission_assigned_to);
        }

        return sortSubmissions(subs);
    }

    const handleChange = (event, newValue) => {
        setFilterType(newValue);
    };

    const activeRequest = useMemo(() => {
        if (!submissions || !clockifyTimer) return null;
        const submission =  submissions.find(s => s.submission_id === clockifyTimer.submission_id);

        return submission;
    }, [clockifyTimerId])

    const renderAdornment = () => {
        if (isLimitedTeamMember) return;
        return isLoadingMembers ? <Skeleton variant="circular" width={40} height={40} />: <TooltipIconButton
            text={"Add New Request"}
            icon= {<Add sx={{ fontSize: '24px'}}/>}
            onClick={() => setOpenAddRequest(true)}
        />
    }

    const renderSubmissions = () => {
        const subs = getSubmissions();
        return (
            <StyledSection title={"Active Requests"} rightAdornment={renderAdornment()}>
                <StyledTextField
                    fullWidth
                    autoFocus
                    key="search"
                    value={searchQuery}
                    label="Search"
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <StyledTabs value={filterType} onChange={handleChange}>
                    <StyledTab label="Assigned To Me" selected={filterType === 0} />
                    {!isLimitedTeamMember && <StyledTab label="Unassigned" selected={filterType === 1} />}
                    {!isLimitedTeamMember && <StyledTab label="All" selected={filterType === 2} /> }
                </StyledTabs>

                {clockifyTimer&& activeRequest &&
                    <Paper variant='outlined' sx={{ marginTop: '20px', paddingLeft: '10px', paddingRight: '10px', borderColor: Colors.orangeColor }}>
                        <List style={{ flex: 1 }}>
                                <ListItem
                                    dense
                                    key={activeRequest.submission_id}
                                    sx={{
                                        backgroundColor: '#fff',
                                        marginBottom: '5px',
                                        paddingRight: '55px',
                                    }}
                                >
                                <ListItemAvatar>
                                        <TimerOutlined sx={{ color: Colors.orangeColor }} />
                                    </ListItemAvatar>
                                <ListItemText
                                    primary={createSubmissionTitle(activeRequest)}
                                    secondary={<><em>{activeRequest.submission_identifiers.site.site_name}</em> | Timer Running: <strong>{elapsedTimeLabel}</strong></>} 
                                />
                                <ListItemSecondaryAction>
                                    <TooltipIconButton
                                        text={'View Details'}
                                        icon= {<AssignmentOutlined sx={{ color: Colors.primaryColor }}
                                        onClick={() => setRequestInView(activeRequest)}
                                    /> } />
                                    </ListItemSecondaryAction>
                                </ListItem>
                        </List>
                    </Paper>
                }

                <Paper variant='outlined' sx={{ marginTop: '20px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {subs.map((submission, index) => (
                            <ListItem
                                dense
                                key={submission.submission_id}
                                sx={{
                                    backgroundColor: '#fff',
                                    marginBottom: '5px',
                                    paddingRight: '55px',
                                    borderBottom: index !== subs.length - 1 ? '1px solid #e0e0e0' : 'none',
                                }}
                            >
                            <ListItemAvatar>
                                    <StyledStatusIcon status={submission.submission_status} />
                                </ListItemAvatar>
                            <ListItemText
                                primary={createSubmissionTitle(submission)}
                                secondary={<><em>{submission.submission_identifiers.site.site_name}</em> | {timeAgo(submission.submission_received_epoch)}</>} 
                            />
                            <ListItemSecondaryAction>
                                <TooltipIconButton
                                    text={'View Details'}
                                    icon= {<AssignmentOutlined sx={{ color: Colors.primaryColor }}
                                    onClick={() => setRequestInView(submission)}
                                /> } />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        
                        {(!subs || subs.length === 0) &&
                            <p>No requests found.</p>
                        }
                    </List>
                </Paper>
            </StyledSection>
        )
    }

    const searchSubmissions = (subs, query) => {
        if (!Array.isArray(subs)) return [];
        if (!query) return subs;
    
        const normalizedQuery = String(query).toLowerCase();
    
        return subs.filter((submission) => {
            if (!submission) return false;
    
            // Safe title
            let title = "";
            if (
                submission.submission_fields &&
                submission.submission_fields.title
            ) {
                title = String(submission.submission_fields.title).toLowerCase();
            }
    
            // Safe site name
            let siteName = "";
            if (
                submission.submission_identifiers &&
                submission.submission_identifiers.site &&
                submission.submission_identifiers.site.site_name
            ) {
                siteName = String(
                    submission.submission_identifiers.site.site_name
                ).toLowerCase();
            }
    
            // Safe submission ID (last 6 chars)
            let subId = "";
            if (submission.submission_id) {
                const idStr = String(submission.submission_id);
                subId = idStr.substring(Math.max(0, idStr.length - 6)).toLowerCase();
            }
    
            return (
                title.includes(normalizedQuery) ||
                siteName.includes(normalizedQuery) ||
                subId.includes(normalizedQuery)
            );
        });
    };
      
    return (
        <StyledPage>
            <h3 style={{ margin: 0, paddingTop: '10px' }}>Team Panel</h3>
            <h5 style={{ margin: 0, paddingBottom: '10px' }}>Good {renderGreeting()}, {getFirstName()}</h5>
            <Box sx={{ marginTop: '0px' }}>
                {renderSubmissions()}
            </Box>

            <ViewRequestModal
                open={!!requestInView}
                onClose={handleViewRequestOnClose}
                request={requestInView}
                timer={clockifyTimer}
            />
            <AdminAddRequestModal
                open={openAddRequest}
                onClose={handleAddRequestOnClose}
            />
        </StyledPage>
       
    )
}

export default withRouter(observer(AdminComponent));

